import { GridColumnProps } from '@progress/kendo-react-grid';
import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import styled from 'styled-components';
import { useEffect, useRef } from 'react';

type Props = {
  data: any[] | undefined;
  columns?: GridColumnProps[] | undefined;
};

const LiveRankingTable = ({ data, columns }: Props) => {
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const gridWrapper = gridWrapperRef.current;
    if (!gridWrapper) return;

    // Kendo Grid scroll sync
    const gridContent = gridWrapper.querySelector('.k-grid-content') as HTMLElement;
    const gridHeaderWrap = gridWrapper.querySelector('.k-grid-header-wrap') as HTMLElement;
    const topScrollbar = gridWrapper.querySelector('.top-scrollbar') as HTMLElement;
    const scrollbarInner = gridWrapper.querySelector('.scrollbar-inner') as HTMLElement;

    // Update scrollbar inner width to match grid content
    const updateScrollbarWidth = () => {
      if (gridContent && scrollbarInner) {
        const scrollWidth = gridContent.scrollWidth;
        scrollbarInner.style.width = `${scrollWidth}px`;
      }
    };

    // Initial update
    updateScrollbarWidth();

    const handleContentScroll = () => {
      if (gridHeaderWrap && gridContent) {
        gridHeaderWrap.scrollLeft = gridContent.scrollLeft;
      }
      if (topScrollbar && gridContent) {
        topScrollbar.scrollLeft = gridContent.scrollLeft;
      }
    };

    const handleHeaderScroll = () => {
      if (gridContent && gridHeaderWrap) {
        gridContent.scrollLeft = gridHeaderWrap.scrollLeft;
      }
      if (topScrollbar && gridHeaderWrap) {
        topScrollbar.scrollLeft = gridHeaderWrap.scrollLeft;
      }
    };

    const handleTopScrollbarScroll = () => {
      if (gridContent && topScrollbar) {
        gridContent.scrollLeft = topScrollbar.scrollLeft;
      }
      if (gridHeaderWrap && topScrollbar) {
        gridHeaderWrap.scrollLeft = topScrollbar.scrollLeft;
      }
    };

    if (gridContent) {
      gridContent.addEventListener('scroll', handleContentScroll);
    }

    if (gridHeaderWrap) {
      gridHeaderWrap.addEventListener('scroll', handleHeaderScroll);
    }

    if (topScrollbar) {
      topScrollbar.addEventListener('scroll', handleTopScrollbarScroll);
    }

    // Update width on window resize
    const handleResize = () => {
      updateScrollbarWidth();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (gridContent) {
        gridContent.removeEventListener('scroll', handleContentScroll);
      }
      if (gridHeaderWrap) {
        gridHeaderWrap.removeEventListener('scroll', handleHeaderScroll);
      }
      if (topScrollbar) {
        topScrollbar.removeEventListener('scroll', handleTopScrollbarScroll);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [data, columns]);

  return (
    <>
      <CardWrapper style={{ paddingTop: '15px' }}>
        <CardSubWrapper className="d-flex"></CardSubWrapper>
        <GridWrapper ref={gridWrapperRef}>
          <TopScrollbarContainer className="top-scrollbar">
            <ScrollbarInner className="scrollbar-inner" />
          </TopScrollbarContainer>
          <CustomDataGrid
            showPagination={false}
            className="custom-data-grid"
            data={data}
            columns={columns}
          />
        </GridWrapper>
      </CardWrapper>
    </>
  );
};

export default LiveRankingTable;

const TopScrollbarContainer = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  margin-bottom: 0;
  width: 100%;
  height: 35px;
  
  &::-webkit-scrollbar {
    height: 20px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 6px;
    
    &:hover {
      background: #555;
    }
  }
  
  /* Firefox scrollbar styling */
  scrollbar-color: #888 #f1f1f1;
  scrollbar-width: thick;
`;

const ScrollbarInner = styled.div`
  height: 1px;
  width: 100%;
  min-width: 100%;
`;

const GridWrapper = styled.div`
  margin-bottom: 10px;
  width: 100%;

  /* Enable horizontal scroll on header wrap */
  .k-grid-header-wrap {
    overflow-x: scroll !important;
    overflow-y: hidden !important;
    
    &::-webkit-scrollbar {
      height: 0px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 3px;
    }
  }

  /* Style the grid content scrollbar to match top scrollbar */
  .k-grid-content {
    &::-webkit-scrollbar {
      height: 20px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 6px;
      
      &:hover {
        background: #555;
      }
    }
    
    /* Firefox scrollbar styling */
    scrollbar-color: #888 #f1f1f1;
    scrollbar-width: thick;
  }

  .k-header {
    align-content: center;
  }
  
  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }
  
  .custom-data-grid {
    th {
      vertical-align: baseline;
      padding: 5px !important;
    }
    .k-virtual-content {
      height: unset !important;
      min-height: unset;
    }
  }
  
  .k-grid-content tr {
    .k-grid-content-sticky {
      :hover {
        background: #f0f0f0 !important;
      }
    }
  }
  
  .k-grid-content tr td.k-grid-content-sticky {
    cursor: unset;
  }
`;